import { useEffect, useState } from 'react'
import { useFetcher, useNavigate } from 'react-router'
import {
	Dialog,
	DialogContent,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn, useDebounce } from '#app/utils/misc.tsx'

type SearchResult = {
	id: string
	name: string
	slug: string
	cuisine: string | null
	consensusScore: number | null
	primaryArea: { name: string } | null
	picks: { title: string }[]
}

type AreaResult = {
	id: string
	name: string
	slug: string
	restaurantCount: number
}

export function GlobalSearch() {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const fetcher = useFetcher<{
		areas: AreaResult[]
		restaurants: SearchResult[]
		error: string | null
	}>()
	const navigate = useNavigate()

	// Debounced search
	const debouncedSearch = useDebounce((searchQuery: string) => {
		if (searchQuery.trim().length > 0) {
			void fetcher.load(
				`/resources/search-restaurants?q=${encodeURIComponent(searchQuery)}`,
			)
		}
	}, 300)

	// Handle input change
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setQuery(value)
		debouncedSearch(value)
	}

	// Handle keyboard shortcut (Cmd+K / Ctrl+K)
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault()
				setOpen((prev) => !prev)
			}
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [])

	// Clear query when modal closes
	useEffect(() => {
		if (!open) {
			setQuery('')
		}
	}, [open])

	// Handle result click
	const handleRestaurantClick = (slug: string) => {
		setOpen(false)
		void navigate(`/london/restaurants/${slug}`)
	}

	const handleAreaClick = (slug: string) => {
		setOpen(false)
		void navigate(`/london/best/restaurants-${slug}`)
	}

	const areas = fetcher.data?.areas ?? []
	const results = fetcher.data?.restaurants ?? []
	const isLoading = fetcher.state === 'loading'
	const hasQuery = query.trim().length > 0

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			{/* Floating Action Button */}
			<DialogTrigger asChild>
				<button
					className={cn(
						'fixed right-6 bottom-6 z-40',
						'flex h-14 w-14 items-center justify-center',
						'rounded-full bg-[#422ad5] text-white',
						'shadow-[0_0_20px_rgba(66,42,213,0.4)]',
						'transition-all duration-200',
						'hover:scale-105 hover:shadow-[0_0_28px_rgba(0,224,255,0.3)]',
						'focus:ring-2 focus:ring-[#00e0ff] focus:ring-offset-2 focus:ring-offset-[#05050a] focus:outline-none',
					)}
					aria-label="Search restaurants"
				>
					<Icon name="magnifying-glass" size="lg" />
				</button>
			</DialogTrigger>

			{/* Modal Content */}
			<DialogContent>
				<div className="p-4">
					{/* Search Input */}
					<div className="relative">
						<Icon
							name="magnifying-glass"
							className="absolute top-1/2 left-3 -translate-y-1/2 text-[#b0b0e0]"
							size="md"
						/>
						<input
							type="text"
							placeholder="Search restaurants..."
							value={query}
							onChange={handleInputChange}
							className={cn(
								'w-full rounded-lg py-3 pr-16 pl-10',
								'border border-[#1a1a2e] bg-[#151525] text-[#f8f8ff]',
								'placeholder:text-[#b0b0e0]/60',
								'focus:border-[#422ad5] focus:ring-1 focus:ring-[#422ad5] focus:outline-none',
							)}
							autoFocus
						/>
						{/* Keyboard hint */}
						<div className="absolute top-1/2 right-3 -translate-y-1/2">
							<kbd className="hidden items-center gap-1 rounded border border-[#2a2a3e] bg-[#1a1a2e] px-2 py-0.5 text-xs text-[#b0b0e0] sm:inline-flex">
								ESC
							</kbd>
						</div>
					</div>

					{/* Results */}
					<div className="mt-4 max-h-[400px] overflow-y-auto">
						{isLoading && hasQuery && (
							<div className="py-8 text-center text-[#b0b0e0]">
								Searching...
							</div>
						)}

						{!isLoading &&
							hasQuery &&
							areas.length === 0 &&
							results.length === 0 && (
								<div className="py-8 text-center text-[#b0b0e0]">
									No matches found for "{query}"
								</div>
							)}

						{!isLoading && (areas.length > 0 || results.length > 0) && (
							<div className="space-y-4">
								{areas.length > 0 && (
									<div>
										<p className="px-2 pb-2 text-[11px] font-semibold tracking-[0.2em] text-[#b0b0e0]/70 uppercase">
											Areas
										</p>
										<ul className="space-y-1">
											{areas.map((area) => (
												<li key={area.id}>
													<button
														onClick={() => handleAreaClick(area.slug)}
														className={cn(
															'w-full rounded-lg px-4 py-3 text-left',
															'bg-transparent hover:bg-[#151525]',
															'transition-colors duration-150',
															'focus:bg-[#151525] focus:outline-none',
														)}
													>
														<div className="flex items-start justify-between gap-3">
															<div className="min-w-0 flex-1">
																<p className="truncate font-semibold text-[#f8f8ff]">
																	{area.name}
																</p>
																<p className="truncate text-sm text-[#b0b0e0]">
																	London area
																</p>
															</div>
															<span className="flex-shrink-0 rounded border border-[#00e0ff]/40 bg-[#00e0ff]/10 px-2 py-1 text-xs font-semibold text-[#00e0ff]">
																{area.restaurantCount} spots
															</span>
														</div>
													</button>
												</li>
											))}
										</ul>
									</div>
								)}

								{results.length > 0 && (
									<div>
										<p className="px-2 pb-2 text-[11px] font-semibold tracking-[0.2em] text-[#b0b0e0]/70 uppercase">
											Restaurants
										</p>
										<ul className="space-y-1">
											{results.map((restaurant) => (
												<li key={restaurant.id}>
													<button
														onClick={() =>
															handleRestaurantClick(restaurant.slug)
														}
														className={cn(
															'w-full rounded-lg px-4 py-3 text-left',
															'bg-transparent hover:bg-[#151525]',
															'transition-colors duration-150',
															'focus:bg-[#151525] focus:outline-none',
														)}
													>
														<div className="flex items-start justify-between gap-3">
															<div className="min-w-0 flex-1">
																<p className="truncate font-semibold text-[#f8f8ff]">
																	{restaurant.name}
																</p>
																<p className="truncate text-sm text-[#b0b0e0]">
																	{restaurant.cuisine}
																	{restaurant.primaryArea &&
																		` · ${restaurant.primaryArea.name}`}
																</p>
																{restaurant.picks[0] && (
																	<p className="mt-1 text-xs text-[#00e0ff]">
																		Try: {restaurant.picks[0].title}
																	</p>
																)}
															</div>
															{restaurant.consensusScore &&
																restaurant.consensusScore > 0 && (
																	<span className="flex-shrink-0 rounded border border-[#422ad5] bg-[#422ad520] px-2 py-1 text-xs font-semibold text-[#f8f8ff]">
																		{restaurant.consensusScore}
																	</span>
																)}
														</div>
													</button>
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						)}

						{!hasQuery && (
							<div className="py-8 text-center text-[#b0b0e0]">
								<p className="text-sm">Start typing to search restaurants</p>
								<p className="mt-2 text-xs opacity-60">
									Search by restaurant, cuisine, or area
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Footer hint */}
				<div className="border-t border-[#1a1a2e] px-4 py-3 text-xs text-[#b0b0e0]/60">
					<span className="hidden sm:inline">
						Press <kbd className="rounded bg-[#1a1a2e] px-1.5 py-0.5">CMD</kbd>{' '}
						+ <kbd className="rounded bg-[#1a1a2e] px-1.5 py-0.5">K</kbd> to
						open search anytime
					</span>
				</div>
			</DialogContent>
		</Dialog>
	)
}
